import { getShippingEligibility } from './proda-shipping.js';

const analytics = window.PRODA_ANALYTICS;

if (analytics && !analytics.storefrontEventsBound) {
  analytics.storefrontEventsBound = true;

  const context = analytics.context || {};
  let observedPurchaseOptions = new WeakMap();
  let observedProductCards = new WeakSet();
  let observedLeadForms = new WeakSet();
  const processedCartEvents = new WeakSet();
  const processedDiscountEvents = new WeakSet();
  const upsellImpressions = new Set();
  const scrollDepthMilestones = new Set();
  const directCommerceThemeId = String(context.theme_id || document.body?.dataset.themeId || '');
  const checkoutAttributeKeys = {
    directCommerce: '_proda_direct_commerce',
    distinctId: '_proda_posthog_distinct_id',
    sessionId: '_proda_posthog_session_id',
    experimentEnvironment: '_proda_experiment_environment',
    experimentAssignments: '_proda_experiment_assignments',
    themeId: '_proda_theme_id',
    qaRun: '_proda_qa_run',
  };
  // Campaign data carried into checkout. A single underscore keeps these
  // readable from Liquid and the Ajax cart API while hiding them from the
  // customer at checkout; they stay visible on the admin order page, so they
  // must remain non-sensitive.
  const checkoutCampaignAttributeKeys = {
    utm_source: '_proda_utm_source',
    utm_medium: '_proda_utm_medium',
    utm_campaign: '_proda_utm_campaign',
    utm_content: '_proda_utm_content',
    utm_term: '_proda_utm_term',
    gclid: '_proda_gclid',
    fbclid: '_proda_fbclid',
    ttclid: '_proda_ttclid',
    wbraid: '_proda_wbraid',
    gbraid: '_proda_gbraid',
    fbc: '_proda_fbc',
    fbp: '_proda_fbp',
    ttp: '_proda_ttp',
    referrer: '_proda_referrer',
    referring_domain: '_proda_referring_domain',
  };
  const checkoutHandoffStorageKey = 'proda_checkout_handoff_synced';
  const marketingOptInCaptureStorageKey = 'proda_marketing_opt_in_captured';
  const storefrontExperimentKeys = new Set([
    'exp-pdp-purchase-framing-v1',
    'exp-pdp-pack-framing-v1',
    'exp-cart-upsell-layout-v1',
    'exp-cart-checkout-clarity-v1',
  ]);
  const capturedExperimentAssignments = new Set();
  let pageContextCaptured = false;
  let featureFlagsBound = false;
  let checkoutHandoffBound = false;
  let scrollDepthBound = false;
  let productCardObserver = null;
  let leadFormObserver = null;
  let offerFallbackCloseBound = false;
  let checkoutHandoffTimer = 0;
  let checkoutHandoffRequest = null;
  let checkoutHandoffAbortController = null;
  let checkoutHandoffFingerprint = '';
  let checkoutHandoffGeneration = 0;
  let checkoutHandoffClearRequest = null;
  let cartRequest = null;
  let checkoutIntentAt = 0;
  let lastCartViewSignature = '';
  let lastCartViewAt = 0;
  let cartState = {
    cart_item_count: Number(context.cart_item_count || 0),
    cart_value_cents: 0,
    cart_contains_subscription: false,
    items: [],
  };

  function isEnabled() {
    return analytics.enabled === true && window.PRODA_TRACKING?.readConsent().analytics === true &&
      window.posthog && typeof window.posthog.capture === 'function';
  }

  function markSemanticAutocaptureBoundaries(root = document) {
    const selectors = [
      '[data-klaviyo-form-id]',
      'button[name="checkout"]',
      'a[href*="/checkout"]',
      '[data-showcase-previous]',
      '[data-showcase-next]',
      '[data-proda-cart-upsell-card] button',
      '[data-proda-cart-upsell-card] a',
      '[data-purchase-type]',
      '[data-pack-quantity]',
      '[data-selling-plan-select]',
      'a[href*="/products/"]',
      'a[href^="http://"]',
      'a[href^="https://"]',
      'form[data-klaviyo-capture]',
      'form[data-proda-offer-form]',
    ].join(',');

    for (const element of root.querySelectorAll?.(selectors) || []) {
      element.setAttribute('data-ph-no-autocapture', 'true');
    }
  }

  function attributionEnvironment() {
    return window.PRODA_TRACKING?.environment(context) || 'unknown';
  }

  function isTargetDirectTheme() {
    // The consumer surface is the real gate. Comparing context.theme_id against
    // directCommerceThemeId was a self-comparison, and it silently disabled the
    // whole identity handoff whenever the context object was missing.
    return document.body?.dataset.prodaSurface === 'consumer';
  }

  function safelyReadStorage(storageName, key) {
    try {
      return window[storageName]?.getItem(key) || '';
    } catch (error) {
      return '';
    }
  }

  function safelyWriteStorage(storageName, key, value) {
    try {
      window[storageName]?.setItem(key, value);
    } catch (error) {
      // Storage can be unavailable in strict privacy modes.
    }
  }

  function safelyRemoveStorage(storageName, key) {
    try {
      window[storageName]?.removeItem(key);
    } catch (error) {
      // Storage can be unavailable in strict privacy modes.
    }
  }

  function normalizeCheckoutAttribute(value, maxLength = 200) {
    return String(value || '')
      .replace(/[\u0000-\u001f\u007f]/g, '')
      .trim()
      .slice(0, maxLength);
  }

  function normalizeExperimentVariant(value) {
    if (value === true) return 'treatment';
    if (value === false) return 'control';
    return normalizeCheckoutAttribute(value, 80);
  }

  function experimentCartHandoff() {
    const runtime = window.PRODA_EXPERIMENTS;
    if (!runtime?.enabled || typeof runtime.getCartHandoff !== 'function') {
      return { assignments: '{}', environment: attributionEnvironment(), themeId: directCommerceThemeId };
    }

    const handoff = runtime.getCartHandoff();
    if (
      handoff?.proda_experiment_environment !== attributionEnvironment() ||
      String(handoff?.proda_experiment_theme_id || '') !== directCommerceThemeId
    ) {
      return { assignments: '{}', environment: attributionEnvironment(), themeId: directCommerceThemeId };
    }

    const safeAssignments = {};
    try {
      const assignments = JSON.parse(String(handoff.proda_experiment_assignments || '{}'));
      if (assignments && typeof assignments === 'object' && !Array.isArray(assignments)) {
        for (const [key, value] of Object.entries(assignments)) {
          const variant = normalizeExperimentVariant(value);
          if (storefrontExperimentKeys.has(key) && variant) safeAssignments[key] = variant;
        }
      }
    } catch (error) {
      // Invalid runtime handoff data stays on an empty attribution baseline.
    }

    return {
      assignments: JSON.stringify(safeAssignments),
      environment: attributionEnvironment(),
      themeId: directCommerceThemeId,
    };
  }

  function experimentEventProperties() {
    if (!isTargetDirectTheme()) return {};

    const handoff = experimentCartHandoff();
    const properties = {
      proda_experiment_environment: handoff.environment,
      proda_experiment_assignments: handoff.assignments,
    };

    try {
      for (const [key, variant] of Object.entries(JSON.parse(handoff.assignments))) {
        properties[`$feature/${key}`] = variant;
      }
    } catch (error) {
      // The sanitized handoff should always be JSON; keep the baseline if it is not.
    }

    return properties;
  }

  function campaignValue(key) {
    const registered = window.posthog?.get_property?.(key);
    if (registered) return normalizeCheckoutAttribute(registered);

    try {
      const fromUrl = new URLSearchParams(window.location.search).get(key);
      if (fromUrl) return normalizeCheckoutAttribute(fromUrl);
    } catch (error) {
      // A malformed query string simply leaves the attribute empty.
    }

    return '';
  }

  function firstPartyCookie(name) {
    try {
      const match = String(document.cookie || '').match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
      return match ? normalizeCheckoutAttribute(decodeURIComponent(match[1])) : '';
    } catch (error) {
      return '';
    }
  }

  function checkoutCampaignAttributes() {
    // Empty values also clear an earlier permitted handoff after a purpose
    // change; merely omitting keys leaves their old values on the cart.
    const attributes = Object.fromEntries(Object.values(checkoutCampaignAttributeKeys).map((key) => [key, '']));
    if (!window.PRODA_TRACKING?.readConsent().marketingAttribution) return attributes;
    // document.referrer is the previous PRODA page on any internal navigation,
    // so an unfiltered fallback would stamp the store's own domain onto the
    // order as its referring domain. Only a cross-origin referrer counts.
    const currentReferrer = document.referrer || '';
    const externalReferrer =
      currentReferrer && safeUrl(currentReferrer)?.origin !== window.location.origin ? currentReferrer : '';
    const rawReferrer = normalizeCheckoutAttribute(
      window.posthog?.get_property?.('landing_referrer') || externalReferrer
    );
    const referrerUrl = safeUrl(rawReferrer);
    const referrer = rawReferrer && referrerUrl ? `${referrerUrl.origin}${referrerUrl.pathname}` : '';

    for (const [key, attributeKey] of Object.entries(checkoutCampaignAttributeKeys)) {
      let value = '';
      if (key === 'referrer') {
        value = referrer;
      } else if (key === 'referring_domain') {
        value = referrer ? normalizeCheckoutAttribute(safeUrl(referrer)?.hostname) : '';
      } else if (key === 'fbc' || key === 'fbp' || key === 'ttp') {
        value = campaignValue(key) || firstPartyCookie(`_${key}`);
      } else {
        value = window.PRODA_TRACKING?.safeAttribution(campaignValue(key)) || '';
      }

      if (value) attributes[attributeKey] = value;
    }

    return attributes;
  }

  function checkoutHandoffAttributes() {
    if (!isEnabled() || !isTargetDirectTheme()) return null;

    const distinctId = normalizeCheckoutAttribute(window.posthog?.get_distinct_id?.());
    const sessionId = normalizeCheckoutAttribute(window.posthog?.get_session_id?.());
    if (!distinctId) return null;
    const experimentHandoff = experimentCartHandoff();

    return {
      [checkoutAttributeKeys.directCommerce]: 'true',
      [checkoutAttributeKeys.distinctId]: distinctId,
      ...(sessionId ? { [checkoutAttributeKeys.sessionId]: sessionId } : {}),
      [checkoutAttributeKeys.experimentEnvironment]: experimentHandoff.environment,
      [checkoutAttributeKeys.experimentAssignments]: experimentHandoff.assignments,
      [checkoutAttributeKeys.themeId]: experimentHandoff.themeId,
      [checkoutAttributeKeys.qaRun]: context.qa_run === 'proda-next-2026-09-08' ? context.qa_run : '',
      ...checkoutCampaignAttributes(),
    };
  }

  function cartUpdateUrl() {
    const root = String(window.Shopify?.routes?.root || '/');
    return `${root.endsWith('/') ? root : `${root}/`}cart/update.js`;
  }

  function persistCheckoutHandoff({ keepalive = false } = {}) {
    const attributes = checkoutHandoffAttributes();
    if (!attributes) return Promise.resolve(false);
    if (checkoutHandoffClearRequest) {
      return checkoutHandoffClearRequest.then(() => persistCheckoutHandoff({ keepalive }));
    }

    const fingerprint = JSON.stringify(attributes);
    if (fingerprint === checkoutHandoffFingerprint) return Promise.resolve(true);
    if (checkoutHandoffRequest) return checkoutHandoffRequest;

    const abortController = new AbortController();
    const generation = checkoutHandoffGeneration;
    checkoutHandoffAbortController = abortController;
    const request = fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      keepalive,
      signal: abortController.signal,
      body: JSON.stringify({ attributes }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart attribution request failed: ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        cartState = normalizeCart(cart);
        if (generation !== checkoutHandoffGeneration || !isEnabled()) return false;
        checkoutHandoffFingerprint = fingerprint;
        safelyWriteStorage('localStorage', checkoutHandoffStorageKey, 'true');
        return true;
      })
      .catch(() => false)
      .finally(() => {
        if (checkoutHandoffAbortController === abortController) {
          checkoutHandoffAbortController = null;
        }
        if (checkoutHandoffRequest !== request) return;
        checkoutHandoffRequest = null;
        const currentAttributes = checkoutHandoffAttributes();
        if (
          generation === checkoutHandoffGeneration &&
          checkoutHandoffFingerprint === fingerprint &&
          currentAttributes &&
          JSON.stringify(currentAttributes) !== checkoutHandoffFingerprint
        ) {
          window.clearTimeout(checkoutHandoffTimer);
          checkoutHandoffTimer = window.setTimeout(() => {
            void persistCheckoutHandoff();
          }, 0);
        }
      });

    checkoutHandoffRequest = request;
    return request;
  }

  function clearCheckoutHandoff() {
    window.clearTimeout(checkoutHandoffTimer);
    checkoutHandoffGeneration += 1;
    if (checkoutHandoffClearRequest) return checkoutHandoffClearRequest;
    const pendingWrite = checkoutHandoffRequest;
    safelyRemoveStorage('localStorage', 'proda_posthog_distinct_id');
    safelyRemoveStorage('localStorage', 'proda_posthog_session_id');

    if (!pendingWrite && !checkoutHandoffFingerprint && !cartState.cart_item_count &&
        !safelyReadStorage('localStorage', checkoutHandoffStorageKey)) {
      return Promise.resolve(false);
    }

    checkoutHandoffFingerprint = '';
    const attributes = Object.fromEntries(
      [...Object.values(checkoutAttributeKeys), ...Object.values(checkoutCampaignAttributeKeys)].map((key) => [key, ''])
    );
    // Let a prior write settle before clearing it. Aborting fetch cannot undo
    // an already accepted server write and would let stale attribution win.
    const clearRequest = Promise.resolve(pendingWrite).catch(() => false).then(() => fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      keepalive: true,
      body: JSON.stringify({ attributes }),
    }))
      .then((response) => {
        if (!response.ok) throw new Error(`Cart attribution clear failed: ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        cartState = normalizeCart(cart);
        checkoutHandoffFingerprint = '';
        safelyRemoveStorage('localStorage', checkoutHandoffStorageKey);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        if (checkoutHandoffClearRequest === clearRequest) checkoutHandoffClearRequest = null;
      });
    checkoutHandoffClearRequest = clearRequest;
    return clearRequest;
  }

  function handleExperimentAssignment(event) {
    if (!isEnabled() || !isTargetDirectTheme()) return;

    void persistCheckoutHandoff({ keepalive: true });

    const assignment = event.detail?.assignment;
    const key = String(assignment?.key || '');
    const variant = normalizeExperimentVariant(assignment?.variant);
    if (!assignment?.resolved || !storefrontExperimentKeys.has(key) || !variant) return;

    const signature = `${key}:${variant}`;
    if (!capturedExperimentAssignments.has(signature)) {
      capturedExperimentAssignments.add(signature);
      capture('experiment_surface_rendered', {
        experiment_key: key,
        experiment_variant: variant,
        experiment_surface: normalizeCheckoutAttribute(window.PRODA_EXPERIMENTS?.definitions?.[key]?.surface, 80),
        experiment_assignment_source: normalizeCheckoutAttribute(assignment.source, 40),
        proda_experiment_environment: attributionEnvironment(),
      });
    }
  }

  function bindCheckoutHandoff() {
    if (checkoutHandoffBound || !isTargetDirectTheme()) return;
    checkoutHandoffBound = true;

    window.PRODA_CHECKOUT_HANDOFF = {
      environment: attributionEnvironment(),
      sync() {
        return persistCheckoutHandoff({ keepalive: true });
      },
    };

    if (typeof window.posthog?.onSessionId === 'function') {
      window.posthog.onSessionId(() => {
        void persistCheckoutHandoff({ keepalive: true });
      });
    }

    // Express and Shop Pay can reach checkout without a cart-lines update, and
    // the distinct id travels as a cart attribute, so keep the load-time write
    // for anyone who already has a cart. Visitors with an empty cart no longer
    // get a cart record created for them.
    if ((cartState?.cart_item_count || 0) > 0) void persistCheckoutHandoff({ keepalive: true });
  }

  function eventProperties(properties = {}) {
    const amountCents = window.PRODA_TRACKING?.amount(properties.value_cents ?? properties.cart_value_cents);
    return {
      site: context.site,
      shopify_shop_id: context.shopify_shop_id,
      storefront_environment: attributionEnvironment(),
      theme_id: context.theme_id,
      theme_name: context.theme_name,
      theme_role: context.theme_role,
      page_type: context.page_type,
      template_name: context.template_name,
      template_suffix: context.template_suffix,
      locale: context.locale,
      country: context.country,
      currency: context.currency,
      customer_state: context.customer_state,
      ...(context.qa_run ? { qa_run: context.qa_run } : {}),
      ...experimentEventProperties(),
      ...properties,
      // One canonical major-unit revenue field. Cart-level events carry
      // cart_value_cents rather than value_cents, and `value` is omitted
      // entirely on events that have no monetary value so it never lands as 0
      // or NaN on a pageview.
      ...(amountCents !== null && amountCents !== undefined
        ? { value: amountCents / 100 }
        : {}),
    };
  }

  function capture(eventName, properties = {}, options) {
    if (!isEnabled()) return;
    window.posthog.capture(eventName, eventProperties(properties), options);
  }

  function safeUrl(value) {
    try {
      return new URL(value, window.location.origin);
    } catch (error) {
      return null;
    }
  }

  function isAmazonHost(hostname) {
    return (
      hostname === 'amzn.to' ||
      hostname === 'a.co' ||
      /(^|\.)amazon\.(com|ca|com\.mx|co\.uk|de|fr|it|es|co\.jp|com\.au)$/.test(hostname)
    );
  }

  function socialPlatformFromHost(hostname) {
    const host = String(hostname || '').replace(/^www\./, '');
    const platforms = {
      'instagram.com': 'instagram',
      'tiktok.com': 'tiktok',
      'facebook.com': 'facebook',
      'youtube.com': 'youtube',
      'youtu.be': 'youtube',
      'x.com': 'twitter',
      'twitter.com': 'twitter',
      'threads.net': 'threads',
      'linkedin.com': 'linkedin',
      'pinterest.com': 'pinterest',
    };
    return platforms[host] || '';
  }

  function sectionId(element) {
    return element.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '') || '';
  }

  function safeIdentifier(value, maxLength = 80) {
    return String(value || '')
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, '')
      .slice(0, maxLength);
  }

  function safeLabel(value, maxLength = 80) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, maxLength);
  }

  function leadFormFacts(form, overrides = {}) {
    const isOfferFallback = form?.matches?.('[data-proda-offer-form]') === true;
    const isFooter = Boolean(form?.closest?.('footer'));
    const formId = safeIdentifier(
      overrides.formId || (isOfferFallback ? 'proda-15-off-fallback' : form?.id || '')
    );

    // The scratch card keeps the offer dialog's form_id so existing insights stay continuous; offer_variant tells it apart.
    const offerVariant = isOfferFallback ? safeLabel(form.getAttribute('data-proda-offer-variant'), 20) : '';
    return {
      form_type: String(overrides.formType || (isOfferFallback ? 'offer_fallback' : 'newsletter')),
      form_source: String(overrides.formSource || (isOfferFallback ? 'designed_offer_form' : 'shopify_native')),
      ...(formId ? { form_id: formId } : {}),
      ...(offerVariant ? { offer_variant: offerVariant } : {}),
      placement: String(overrides.placement || (isOfferFallback ? 'offer_dialog' : isFooter ? 'footer' : sectionId(form))),
    };
  }

  function newEventId() {
    if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
    return `evt_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
  }

  function analyticsTrackingAllowed() {
    return window.PRODA_TRACKING?.readConsent().onsiteMarketing === true;
  }

  function queueKlaviyoOnsite(method, ...args) {
    return window.PRODA_TRACKING?.queueKlaviyo(method, ...args);
  }

  function klaviyoConsentChannels(detail) {
    const metadata = detail?.metaData && typeof detail.metaData === 'object' ? detail.metaData : {};
    const keys = Object.keys(metadata).map((key) => String(key).toLowerCase());
    const channels = [];

    if (keys.some((key) => key === '$email' || key === 'email')) channels.push('email');
    if (keys.some((key) => key === '$phone_number' || key === 'phone_number' || key === 'phone')) {
      channels.push('sms');
    }
    if (!channels.length && safeIdentifier(detail?.formId) === 'proda-15-off-fallback') channels.push('email');

    return channels;
  }

  function productFilterTypes(filters) {
    if (!Array.isArray(filters)) return [];
    return [
      ...new Set(
        filters.flatMap((filter) =>
          filter && typeof filter === 'object'
            ? Object.keys(filter)
                .map((key) => safeLabel(key, 40))
                .filter(Boolean)
            : []
        )
      ),
    ];
  }

  function numberFrom(value, fallback = 0) {
    if (value === null || value === undefined || typeof value === 'boolean' ||
        (typeof value === 'string' && value.trim() === '')) return fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function boolFrom(value) {
    return value === true || value === 'true';
  }

  function productFromElement(element) {
    const container =
      element.closest('[data-proda-cart-upsell-card], product-card, product-card-link, .product-card, .card') ||
      element.parentElement;
    const productLink =
      (element.matches('a[href*="/products/"]') ? element : null) || container?.querySelector('a[href*="/products/"]');
    const productUrl = safeUrl(productLink?.href || '');
    const productHandle =
      element.getAttribute('data-product-handle') ||
      container?.getAttribute('data-product-handle') ||
      productUrl?.pathname.match(/\/products\/([^/]+)/)?.[1] ||
      (context.page_type === 'product' ? context.product_handle : '') ||
      '';
    const imageAlt = container?.querySelector('img[alt]')?.getAttribute('alt')?.trim() || '';
    const productAriaLabel = productLink?.getAttribute('aria-label')?.trim() || '';
    const cleanedProductAriaLabel = productAriaLabel
      .replace(/^view\s+/i, '')
      .replace(/\s+purchase options$/i, '')
      .trim();
    const title =
      element.getAttribute('data-product-title') ||
      container?.getAttribute('data-product-title') ||
      (context.page_type === 'product' ? context.product_title : '') ||
      cleanedProductAriaLabel ||
      imageAlt ||
      '';
    const productId =
      element.getAttribute('data-product-id') ||
      container?.getAttribute('data-product-id') ||
      (context.page_type === 'product' ? context.product_id : '') ||
      '';

    return {
      ...(productId ? { product_id: String(productId) } : {}),
      ...(productHandle ? { product_handle: String(productHandle) } : {}),
      ...(title ? { product_title: String(title) } : {}),
    };
  }

  function normalizeCart(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return {
      cart_item_count: numberFrom(
        cart?.item_count,
        items.reduce((total, item) => total + numberFrom(item.quantity), 0)
      ),
      cart_value_cents: numberFrom(cart?.total_price),
      cart_contains_subscription: items.some((item) => Boolean(item.selling_plan_allocation)),
      items,
    };
  }

  function cartLineFacts(item) {
    if (!item || typeof item !== 'object') return {};
    const sellingPlan = item.selling_plan_allocation?.selling_plan;
    const quantity = Math.max(0, numberFrom(item.quantity));

    return {
      ...(item.product_id ? { product_id: String(item.product_id) } : {}),
      ...(item.handle ? { product_handle: String(item.handle) } : {}),
      ...(item.product_title ? { product_title: String(item.product_title).slice(0, 160) } : {}),
      ...(item.variant_id || item.id ? { variant_id: String(item.variant_id || item.id) } : {}),
      quantity,
      case_quantity: quantity,
      can_count: quantity * 12,
      purchase_type: sellingPlan?.id ? 'subscription' : 'one_time',
      ...(sellingPlan?.id ? { selling_plan_id: String(sellingPlan.id) } : {}),
      ...(sellingPlan?.name ? { selling_plan_name: String(sellingPlan.name).slice(0, 120) } : {}),
      unit_price_cents: numberFrom(item.final_price, numberFrom(item.price)),
      line_value_cents: numberFrom(item.final_line_price, numberFrom(item.line_price)),
    };
  }

  function cartLineSellingPlanId(item) {
    return String(item?.selling_plan_allocation?.selling_plan?.id || '');
  }

  function comparableCartLineProperties(item) {
    const properties = item?.properties;
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return '{}';

    return JSON.stringify(
      Object.fromEntries(
        Object.entries(properties)
          .filter(([key]) => key !== 'Pack size')
          .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
      )
    );
  }

  function findMatchingCartLine(items, { lineId = '', variantId = '', sellingPlanId = '', previousLine = null } = {}) {
    if (!Array.isArray(items)) return null;

    const exactLine = items.find((item) => String(item?.key || item?.id || '') === String(lineId));
    if (exactLine) return exactLine;

    const stableVariantId = String(variantId || previousLine?.variant_id || previousLine?.id || '');
    if (!stableVariantId) return null;

    const candidates = items.filter(
      (item) =>
        String(item?.variant_id || item?.id || '') === stableVariantId &&
        cartLineSellingPlanId(item) === String(sellingPlanId)
    );
    if (candidates.length <= 1 || !previousLine) return candidates[0] || null;

    const previousProperties = comparableCartLineProperties(previousLine);
    return (
      candidates.find((item) => comparableCartLineProperties(item) === previousProperties) || candidates[0] || null
    );
  }

  function cartQuantityTransition({
    action = 'update',
    requestedQuantity = 0,
    previousLineQuantity = 0,
    resultingLineQuantity = 0,
    previousCartItemCount = 0,
    nextCartItemCount = 0,
  } = {}) {
    const isRemoval = action === 'remove' || action === 'clear';
    const quantityDelta = numberFrom(nextCartItemCount) - numberFrom(previousCartItemCount);
    const newQuantity = isRemoval
      ? 0
      : Math.max(0, numberFrom(resultingLineQuantity, numberFrom(requestedQuantity)));
    const previousQuantity = isRemoval
      ? Math.max(0, numberFrom(previousLineQuantity, Math.abs(Math.min(0, quantityDelta))))
      : Math.max(0, newQuantity - quantityDelta);

    return {
      isRemoval,
      previousQuantity,
      newQuantity,
      quantityDelta,
    };
  }

  function shippingThresholdProperties(snapshot = cartState) {
    const shipping = getShippingEligibility({ items: snapshot?.items || [] });
    if (!shipping) return {};
    return { shipping_offer_enabled: shipping.enabled, shipping_eligible_case_count: shipping.eligibleCaseCount,
      shipping_min_cases: shipping.minCases, shipping_remaining_cases: shipping.remainingCases,
      shipping_offer_qualified: shipping.qualifiesForOffer, shipping_includes_subscriptions: shipping.includesSubscriptions,
      shipping_whole_cart_eligible: shipping.wholeCartEligibleForOffer, shipping_excluded_item_quantity: shipping.excludedItemQuantity };
  }

  async function fetchCartState({ fresh = false } = {}) {
    if (cartRequest && !fresh) return cartRequest;
    if (cartRequest && fresh) await cartRequest;

    cartRequest = fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        cartState = normalizeCart(cart);
        return cartState;
      })
      .catch(() => cartState)
      .finally(() => {
        cartRequest = null;
      });

    return cartRequest;
  }

  function sourceFromElement(element) {
    if (!element) return context.page_type === 'cart' ? 'cart_page' : 'theme';
    if (element.closest('[data-proda-cart-upsell]')) return 'cart_upsell';
    if (element.closest('.sticky-add-to-cart')) return 'sticky_bar';
    if (element.closest('quick-add-component, .quick-add')) return 'quick_add';
    if (element.closest('theme-drawer#cart-drawer')) return 'cart_drawer';
    if (element.closest('product-form-component')) return 'pdp';
    return context.page_type === 'cart' ? 'cart_page' : 'theme';
  }

  function purchaseFromForm(formComponent) {
    const planInput = formComponent?.querySelector('input[name="selling_plan"]');
    const planSelect = formComponent?.querySelector('[data-selling-plan-select]');
    const selectedPlan = planSelect?.selectedOptions?.[0];
    const isCartUpsell = formComponent?.matches?.('[data-proda-cart-upsell]') === true;
    const sellingPlanId = String(planInput?.value || (isCartUpsell ? formComponent?.dataset.sellingPlanId : '') || '');
    const sellingPlanName = String(
      selectedPlan?.dataset.planName || (isCartUpsell ? formComponent?.dataset.sellingPlanName : '') || ''
    );
    const caseQuantity = Math.max(1, numberFrom(formComponent?.querySelector('input[name="quantity"]')?.value, 1));

    return {
      purchase_type: sellingPlanId ? 'subscription' : 'one_time',
      case_quantity: caseQuantity,
      can_count: caseQuantity * 12,
      ...(sellingPlanId ? { selling_plan_id: sellingPlanId } : {}),
      ...(sellingPlanId && sellingPlanName ? { selling_plan_name: sellingPlanName } : {}),
    };
  }

  function capturePageContext() {
    if (!isEnabled() || pageContextCaptured) return;
    pageContextCaptured = true;

    if (context.page_type === 'product') {
      capture('product_viewed', {
        product_id: context.product_id,
        product_title: context.product_title,
        product_handle: context.product_handle,
        product_type: context.product_type,
        product_vendor: context.product_vendor,
        variant_id: context.variant_id,
        value_cents: context.product_price_cents,
        product_available: context.product_available,
      });
    }

    if (context.page_type === 'collection') {
      capture('collection_viewed', {
        collection_id: context.collection_id,
        collection_title: context.collection_title,
        collection_handle: context.collection_handle,
      });
    }

    if (context.page_type === 'cart') {
      void captureCartViewed('cart_page');
    }

    if (context.page_type === 'search') {
      const searchTerm = new URLSearchParams(window.location.search).get('q') || '';
      capture('search_submitted', {
        search_query_length: searchTerm.trim().length,
      });
    }

    const query = new URLSearchParams(window.location.search);
    if (query.get('customer_posted') === 'true') {
      const successKey = `proda_form_success:${window.location.pathname}:${window.location.search}`;
      if (safelyReadStorage('sessionStorage', successKey) !== 'true') {
        safelyWriteStorage('sessionStorage', successKey, 'true');
        let alreadyCaptured = false;
        const recentCapture = safelyReadStorage('sessionStorage', marketingOptInCaptureStorageKey);
        safelyRemoveStorage('sessionStorage', marketingOptInCaptureStorageKey);

        if (recentCapture) {
          try {
            const parsedCapture = JSON.parse(recentCapture);
            alreadyCaptured =
              parsedCapture.path === window.location.pathname &&
              Date.now() - Number(parsedCapture.captured_at || 0) <= 120000;
          } catch (error) {
            // Invalid or stale suppression data should not hide a confirmed signup.
          }
        }

        if (!alreadyCaptured) {
          capture('website_form_submitted', {
            form_type: 'newsletter',
            form_source: 'shopify_native',
            placement: 'redirect_confirmation',
            consent_channels: ['email'],
            completion_source: 'shopify_confirmation',
          });
        }
      }
    }
  }

  function bindFeatureFlags() {
    window.PRODA_FEATURES = {
      get(key) {
        return typeof window.posthog?.getFeatureFlag === 'function' ? window.posthog.getFeatureFlag(key) : undefined;
      },
      isEnabled(key) {
        return typeof window.posthog?.isFeatureEnabled === 'function' && window.posthog.isFeatureEnabled(key) === true;
      },
      onChange(callback) {
        if (typeof callback !== 'function' || typeof window.posthog?.onFeatureFlags !== 'function') return;
        window.posthog.onFeatureFlags(callback);
      },
    };

    if (featureFlagsBound || typeof window.posthog?.onFeatureFlags !== 'function') return;
    featureFlagsBound = true;
    window.posthog.onFeatureFlags((flags, variants) => {
      document.dispatchEvent(
        new CustomEvent('proda:feature-flags:ready', {
          detail: { flags, variants },
        })
      );
    });
  }

  function capturePurchaseOptionsViewed() {
    for (const component of document.querySelectorAll('proda-purchase-options')) {
      const options = component.querySelectorAll('[data-selling-plan-select] option');
      const productId = String(component.dataset.productId || context.product_id || '');
      const productHandle = String(component.dataset.productHandle || context.product_handle || '');
      const variantId = String(component.dataset.variantId || context.variant_id || '');
      const selectedPlan = component.querySelector('[data-selling-plan-select]')?.selectedOptions?.[0];
      const packSizeOptions = [...component.querySelectorAll('[data-pack-quantity]')]
        .map((input) => numberFrom(input.dataset.canCount, numberFrom(input.value) * 12))
        .filter((value) => value > 0);
      const fingerprint = JSON.stringify({
        productId,
        variantId,
        purchaseType: component.dataset.purchaseType || 'one_time',
        sellingPlanId: component.dataset.sellingPlanId || '',
        caseQuantity: numberFrom(component.dataset.caseQuantity, 1),
        options: [...options].map((option) => [option.value, option.dataset.planPriceCents]),
      });
      if (observedPurchaseOptions.get(component) === fingerprint) continue;
      observedPurchaseOptions.set(component, fingerprint);

      capture('purchase_option_viewed', {
        product_id: productId,
        product_handle: productHandle,
        variant_id: variantId,
        subscription_plan_count: options.length,
        one_time_price_cents: numberFrom(component.dataset.oneTimePriceCents),
        subscription_price_cents: numberFrom(component.dataset.subscriptionPriceCents),
        one_time_available: Boolean(component.querySelector('[data-purchase-type="one_time"]')),
        pack_size_options: packSizeOptions,
        selected_purchase_type: String(component.dataset.purchaseType || 'one_time'),
        selected_selling_plan_id: String(component.dataset.sellingPlanId || ''),
        selected_selling_plan_name: String(component.dataset.sellingPlanName || ''),
        selected_savings_percent: numberFrom(selectedPlan?.dataset.planSavingsPercent),
        selected_case_quantity: Math.max(1, numberFrom(component.dataset.caseQuantity, 1)),
        selected_can_count: Math.max(12, numberFrom(component.dataset.canCount, 12)),
        source: 'pdp',
      });
    }
  }

  function captureProductCardImpressions(root = document) {
    const cards = root.querySelectorAll?.('[data-proda-product-impression]') || [];
    if (!cards.length) return;

    if (!('IntersectionObserver' in window)) {
      for (const card of cards) captureProductCardImpression(card);
      return;
    }

    if (!productCardObserver) {
      productCardObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.55) continue;
            captureProductCardImpression(entry.target);
            productCardObserver.unobserve(entry.target);
          }
        },
        { threshold: [0.55] }
      );
    }

    for (const card of cards) {
      if (!observedProductCards.has(card)) productCardObserver.observe(card);
    }
  }

  function captureProductCardImpression(card) {
    if (!(card instanceof Element) || observedProductCards.has(card)) return;
    observedProductCards.add(card);

    capture('product_card_viewed', {
      ...productFromElement(card),
      source: 'homepage_showcase',
      placement: sectionId(card),
      product_position: numberFrom(card.getAttribute('data-slide-index')) + 1,
      product_kind: String(card.getAttribute('data-product-kind') || ''),
      product_available: boolFrom(card.getAttribute('data-product-available')),
      value_cents: numberFrom(card.getAttribute('data-value-cents')),
      initially_focused: card.hasAttribute('data-initial'),
    });
  }

  function captureLeadFormImpressions(root = document) {
    const forms = root.querySelectorAll?.('form.email-signup__form, form[data-proda-offer-form]') || [];
    if (!forms.length) return;

    if (!('IntersectionObserver' in window)) {
      for (const form of forms) {
        if (form.getClientRects().length) captureLeadFormImpression(form);
      }
      return;
    }

    if (!leadFormObserver) {
      leadFormObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
            captureLeadFormImpression(entry.target);
            leadFormObserver.unobserve(entry.target);
          }
        },
        { threshold: [0.5] }
      );
    }

    for (const form of forms) {
      if (!observedLeadForms.has(form)) leadFormObserver.observe(form);
    }
  }

  function captureLeadFormImpression(form) {
    if (!(form instanceof HTMLFormElement) || observedLeadForms.has(form)) return;
    observedLeadForms.add(form);
    capture('website_form_viewed', leadFormFacts(form));
  }

  function bindOfferFallbackClose() {
    if (offerFallbackCloseBound) return;
    const fallback = document.querySelector('[data-proda-offer-fallback]');
    if (!(fallback instanceof HTMLDialogElement)) return;

    offerFallbackCloseBound = true;
    fallback.addEventListener('close', () => {
      capture('website_form_closed', {
        ...leadFormFacts(fallback.querySelector('form[data-proda-offer-form]')),
        close_source: 'dialog_close',
      });
    });
  }

  function handleScrollDepth() {
    const documentHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body?.scrollHeight || 0
    );
    const viewportBottom = window.scrollY + window.innerHeight;
    const scrollableDistance = Math.max(1, documentHeight - window.innerHeight);
    const depth = Math.min(100, Math.round(((viewportBottom - window.innerHeight) / scrollableDistance) * 100));

    for (const milestone of [25, 50, 75, 90]) {
      if (depth < milestone || scrollDepthMilestones.has(milestone)) continue;
      scrollDepthMilestones.add(milestone);
      capture('scroll_depth_reached', {
        depth_percent: milestone,
        page_path: window.location.pathname,
      });
    }
  }

  function bindScrollDepthTracking() {
    if (scrollDepthBound) return;
    scrollDepthBound = true;
    window.addEventListener('scroll', handleScrollDepth, { passive: true });
    handleScrollDepth();
  }

  async function captureCartViewed(source) {
    const snapshot = await fetchCartState();
    const signature = `${snapshot.cart_item_count}:${snapshot.cart_value_cents}:${snapshot.cart_contains_subscription}`;
    const now = Date.now();
    if (signature === lastCartViewSignature && now - lastCartViewAt < 1000) return;
    lastCartViewSignature = signature;
    lastCartViewAt = now;
    capture('cart_viewed', {
      source,
      cart_item_count: snapshot.cart_item_count,
      cart_value_cents: snapshot.cart_value_cents,
      cart_contains_subscription: snapshot.cart_contains_subscription,
      ...shippingThresholdProperties(snapshot),
    });
    captureUpsellImpressions(snapshot);
  }

  function captureUpsellImpressions(snapshot = cartState) {
    const drawer = document.querySelector('theme-drawer#cart-drawer[open]');
    if (!drawer) return;

    for (const card of drawer.querySelectorAll('[data-proda-cart-upsell-card]')) {
      const impressionKey = `${card.dataset.offerId || card.dataset.productId}:${snapshot.cart_item_count}:${snapshot.cart_value_cents}`;
      if (upsellImpressions.has(impressionKey)) continue;
      upsellImpressions.add(impressionKey);

      capture('upsell_offer_viewed', {
        offer_id: String(card.dataset.offerId || ''),
        product_id: String(card.dataset.productId || ''),
        product_handle: String(card.dataset.productHandle || ''),
        variant_id: String(card.dataset.variantId || ''),
        value_cents: numberFrom(card.dataset.valueCents),
        purchase_type: String(card.dataset.purchaseType || 'one_time'),
        selling_plan_id: String(card.dataset.sellingPlanId || ''),
        selling_plan_name: String(card.dataset.sellingPlanName || ''),
        savings_percent: numberFrom(card.dataset.savingsPercent),
        placement: 'cart_drawer',
        source: 'cart_upsell',
        cart_item_count: snapshot.cart_item_count,
        cart_value_cents: snapshot.cart_value_cents,
        cart_contains_subscription: snapshot.cart_contains_subscription,
      });
    }
  }

  function handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const offerTrigger = target.closest('[data-klaviyo-form-id]');
    if (offerTrigger) {
      capture('website_offer_clicked', {
        offer_id: safeIdentifier(offerTrigger.getAttribute('data-klaviyo-form-id')),
        offer_type: 'newsletter_discount',
        placement: offerTrigger.closest('#proda-deal-banner-slot') ? 'deal_banner' : sectionId(offerTrigger),
      });
    }

    const checkoutTarget = target.closest('button[name="checkout"], a[href*="/checkout"]');
    if (checkoutTarget) {
      checkoutIntentAt = Date.now();
      void persistCheckoutHandoff({ keepalive: true });
      capture(
        'checkout_clicked',
        {
          source: checkoutTarget.getAttribute('data-proda-checkout-source') || sourceFromElement(checkoutTarget),
          cart_item_count: numberFrom(checkoutTarget.getAttribute('data-cart-item-count'), cartState.cart_item_count),
          cart_value_cents: numberFrom(checkoutTarget.getAttribute('data-cart-value-cents'), cartState.cart_value_cents),
          cart_contains_subscription: boolFrom(
            checkoutTarget.getAttribute('data-cart-contains-subscription') ?? cartState.cart_contains_subscription
          ),
          ...shippingThresholdProperties(cartState),
        },
        { transport: 'sendBeacon', send_instantly: true }
      );
      return;
    }

    const showcaseNavigation = target.closest('[data-showcase-previous], [data-showcase-next]');
    if (showcaseNavigation) {
      const showcase = showcaseNavigation.closest('.proda-showcase');
      const activeSlide = showcase?.querySelector('[data-showcase-slide][data-active="true"]');
      capture('product_showcase_navigated', {
        ...productFromElement(activeSlide || showcaseNavigation),
        source: 'homepage_showcase',
        placement: sectionId(showcaseNavigation),
        direction: showcaseNavigation.hasAttribute('data-showcase-next') ? 'next' : 'previous',
        from_position: numberFrom(activeSlide?.getAttribute('data-slide-index')) + 1,
      });
      return;
    }

    const upsellCard = target.closest('[data-proda-cart-upsell-card]');
    if (upsellCard) {
      const purchaseType = String(upsellCard.dataset.purchaseType || 'one_time');
      const action = target.closest('button[name="add"]')
        ? purchaseType === 'subscription'
          ? 'add_subscription'
          : 'add_one_time'
        : target.closest('a[href]')
          ? 'view_product'
          : '';
      if (action) {
        capture('upsell_offer_selected', {
          offer_id: String(upsellCard.dataset.offerId || ''),
          product_id: String(upsellCard.dataset.productId || ''),
          product_handle: String(upsellCard.dataset.productHandle || ''),
          variant_id: String(upsellCard.dataset.variantId || ''),
          value_cents: numberFrom(upsellCard.dataset.valueCents),
          purchase_type: purchaseType,
          selling_plan_id: String(upsellCard.dataset.sellingPlanId || ''),
          selling_plan_name: String(upsellCard.dataset.sellingPlanName || ''),
          savings_percent: numberFrom(upsellCard.dataset.savingsPercent),
          placement: 'cart_drawer',
          source: 'cart_upsell',
          action,
          cart_item_count: cartState.cart_item_count,
          cart_value_cents: cartState.cart_value_cents,
          cart_contains_subscription: cartState.cart_contains_subscription,
        });
      }
      if (action === 'add_one_time' || action === 'add_subscription') return;
    }

    const link = target.closest('a[href]');
    if (!link) return;
    const destination = safeUrl(link.href);
    if (!destination) return;

    if (isAmazonHost(destination.hostname)) {
      const amazonProduct = productFromElement(link);
      const asin =
        String(link.getAttribute('data-amazon-asin') || '').trim() ||
        destination.pathname.match(/\/dp\/([A-Z0-9]{10})/)?.[1] ||
        '';
      // A stable placement token, because sectionId() returns a Shopify section
      // id that changes on every template edit.
      const placement = String(link.getAttribute('data-placement') || '').trim() || sectionId(link);
      // Consumer and wholesale products share Amazon links, so without the
      // surface the tile mixes B2B and DTC intent.
      const surface = String(document.body?.dataset.prodaSurface || '');
      const variantId = String(link.getAttribute('data-variant-id') || '');
      const sku = String(link.getAttribute('data-sku') || '');
      // Null rather than a 0 fallback: a malformed attribute would otherwise
      // publish value 0 on the store's headline revenue-intent event.
      const valueCentsAttribute = link.getAttribute('data-value-cents');
      const parsedValueCents = Number(valueCentsAttribute);
      const valueCents =
        valueCentsAttribute === null || valueCentsAttribute.trim() === '' || !Number.isFinite(parsedValueCents)
          ? null
          : parsedValueCents;

      capture(
        'amazon_outbound_clicked',
        {
          ...amazonProduct,
          source: context.page_type === 'product' ? 'pdp' : 'product_showcase',
          placement,
          ...(surface ? { surface } : {}),
          ...(asin ? { asin } : {}),
          ...(variantId ? { variant_id: variantId } : {}),
          ...(sku ? { sku } : {}),
          ...(valueCents === null ? {} : { value_cents: valueCents }),
          destination_host: destination.hostname,
          destination_path: destination.pathname,
          cta_label: String(link.textContent || '').trim().slice(0, 80),
          event_id: newEventId(),
        },
        { transport: 'sendBeacon', send_instantly: true }
      );

      // Amazon is the revenue channel, so the same click has to be visible to
      // the send engine. No $value: Klaviyo would sum it into the conversion
      // value column and render a modeled number as attributed revenue.
      if (analyticsTrackingAllowed()) {
        queueKlaviyoOnsite('track', 'Clicked to Amazon', {
          ASIN: asin,
          'Product Handle': amazonProduct.product_handle || '',
          Surface: surface,
          Placement: placement,
        });
      }
      return;
    }

    if (destination.origin === window.location.origin && destination.pathname.includes('/products/')) {
      const showcaseSlide = link.closest('[data-showcase-slide]');
      const source =
        link.getAttribute('data-product-link-source') ||
        (link.closest('.proda-showcase')
          ? 'homepage_showcase'
          : link.closest('flavor-selector')
            ? 'pdp_flavor_selector'
            : context.page_type === 'collection'
              ? 'collection_grid'
              : 'internal_product_link');
      capture(
        'product_selected',
        {
          ...productFromElement(link),
          source,
          placement: sectionId(link),
          destination_path: destination.pathname,
          link_action: String(link.getAttribute('data-product-link-action') || 'link'),
          ...(showcaseSlide
            ? {
                product_position: numberFrom(showcaseSlide.getAttribute('data-slide-index')) + 1,
                product_kind: String(showcaseSlide.getAttribute('data-product-kind') || ''),
                product_available: boolFrom(showcaseSlide.getAttribute('data-product-available')),
              }
            : {}),
        },
        { transport: 'sendBeacon', send_instantly: true }
      );
      return;
    }

    // Social anchors authored in merchant rich text carry no data attribute, so
    // fall back to the destination host rather than counting them as generic
    // outbound clicks.
    const socialPlatform =
      link.getAttribute('data-social-platform') || socialPlatformFromHost(destination.hostname);
    if (socialPlatform) {
      capture(
        'social_link_clicked',
        { platform: socialPlatform, section_id: sectionId(link) },
        { transport: 'sendBeacon', send_instantly: true }
      );
      return;
    }

    // The store locator emits its own semantic events from a bubble-phase
    // handler, and this listener is capture-phase, so without this the same
    // click counts twice.
    if (link.closest('proda-store-locator')) return;

    if (destination.origin !== window.location.origin && /^https?:$/.test(destination.protocol)) {
      capture(
        'outbound_link_clicked',
        {
          source: sourceFromElement(link),
          placement: sectionId(link),
          destination_host: destination.hostname,
          destination_path: destination.pathname,
          link_text: String(link.textContent || '').trim().slice(0, 80),
        },
        { transport: 'sendBeacon', send_instantly: true }
      );
    }
  }

  function handleSubmit(event) {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form) return;

    const checkoutSubmitter = event.submitter instanceof Element && event.submitter.matches('[name="checkout"]');
    const checkoutAction = safeUrl(form.action)?.pathname.endsWith('/checkout');
    if (checkoutSubmitter || checkoutAction) {
      void persistCheckoutHandoff({ keepalive: true });
      return;
    }

    const isNewsletter = form.classList.contains('email-signup__form');
    const isOfferFallback = form.matches('[data-proda-offer-form]');
    if (!isNewsletter && !isOfferFallback) return;
    if (isOfferFallback && form.hasAttribute('data-proda-native-handoff')) return;

    const offerStep = isOfferFallback
      ? safeLabel(form.closest('[data-proda-offer-fallback]')?.getAttribute('data-proda-offer-step'), 20) || 'email'
      : '';
    // The scratch card asks for email and mobile in one submit.
    const consentChannels = isOfferFallback && offerStep === 'sms' ? ['sms'] : isOfferFallback && offerStep === 'scratch' ? ['email', 'sms'] : ['email'];

    capture(
      'website_form_attempted',
      {
        ...leadFormFacts(form),
        consent_channels: consentChannels,
        ...(offerStep ? { form_step: offerStep } : {}),
      },
      { transport: 'sendBeacon', send_instantly: true }
    );
  }

  function handleKlaviyoForm(event) {
    const detail = event.detail || {};
    const eventType = String(detail.type || '');
    const formId = safeIdentifier(detail.formId);
    const placement =
      formId === 'proda-15-off-fallback' ? 'offer_dialog' : eventType === 'embedOpen' ? 'embedded_form' : 'popup';
    const baseProperties = {
      form_type: formId === 'proda-15-off-fallback' ? 'offer_fallback' : 'klaviyo_signup',
      form_source: formId === 'proda-15-off-fallback' ? 'designed_offer_form' : 'klaviyo',
      ...(formId ? { form_id: formId } : {}),
      placement,
    };

    if (eventType === 'open' || eventType === 'embedOpen') {
      capture('website_form_viewed', baseProperties);
      return;
    }

    if (eventType === 'close') {
      capture('website_form_closed', { ...baseProperties, close_source: 'klaviyo_event' });
      return;
    }

    if (eventType === 'redirectedToUrl') {
      capture(
        'website_form_redirected',
        baseProperties,
        { transport: 'sendBeacon', send_instantly: true }
      );
      return;
    }

    if (eventType !== 'submit' && eventType !== 'stepSubmit') return;

    const consentChannels = klaviyoConsentChannels(detail);
    const metadata = detail.metaData && typeof detail.metaData === 'object' ? detail.metaData : {};
    const stepName = safeLabel(metadata.stepName);
    capture(
      eventType === 'submit' ? 'website_form_submitted' : 'website_form_step_submitted',
      {
        ...baseProperties,
        ...(consentChannels.length ? { consent_channels: consentChannels } : {}),
        ...(stepName ? { form_step: stepName } : {}),
      },
      { transport: 'sendBeacon', send_instantly: true }
    );
  }

  function offerContextProperties(detail) {
    // Campaign labels must match an owned form/context/code tuple. Never copy
    // arbitrary query text or customer fields into offer analytics.
    if (detail.form_id === 'proda-15-off-fallback' &&
        detail.form_source === 'facebook_dm_offer_form' &&
        detail.offer_context === 'facebook_dm_15' && detail.offer_code === 'PRODADM15') {
      return { offer_context: 'facebook_dm_15', offer_code: 'PRODADM15' };
    }
    return {};
  }

  function handleMarketingOptInResult(event) {
    const detail = event.detail && typeof event.detail === 'object' ? event.detail : {};
    const leadCaptureId = safeIdentifier(detail.lead_capture_id, 80);
    const requestedChannels = Array.isArray(detail.consent_channels_requested)
      ? detail.consent_channels_requested.map((channel) => safeLabel(channel, 20)).filter(Boolean)
      : [];
    const acceptedChannels = Array.isArray(detail.channels_accepted)
      ? detail.channels_accepted.map((channel) => safeLabel(channel, 20)).filter(Boolean)
      : [];
    const result = safeLabel(detail.result, 40) || 'unknown';
    const formId = safeIdentifier(detail.form_id, 80);
    const acceptedVia = safeLabel(detail.accepted_via, 40);
    const klaviyoStatus = safeLabel(detail.klaviyo_status, 40);
    const shopifyStatus = safeLabel(detail.shopify_status, 40);
    const offerVariant = detail.offer_variant === 'scratch' ? 'scratch' : '';
    const baseProperties = {
      form_type: formId === 'proda-15-off-fallback' ? 'offer_fallback' : 'newsletter',
      form_source: safeLabel(detail.form_source, 80) || 'shopify_native',
      ...offerContextProperties(detail),
      ...(formId ? { form_id: formId } : {}),
      ...(offerVariant ? { offer_variant: offerVariant } : {}),
      placement: safeLabel(detail.placement, 40) || 'unknown',
      ...(leadCaptureId ? { lead_capture_id: leadCaptureId } : {}),
      consent_channels_requested: requestedChannels,
      consent_channels: acceptedChannels,
      opt_in_state: result,
      ...(acceptedVia ? { accepted_via: acceptedVia } : {}),
      ...(klaviyoStatus ? { klaviyo_status: klaviyoStatus } : {}),
      ...(shopifyStatus ? { shopify_status: shopifyStatus } : {}),
      schema_version: safeLabel(detail.schema_version, 20) || '1_0',
    };

    capture('marketing_opt_in_request_result', baseProperties, { transport: 'sendBeacon', send_instantly: true });

    if (result === 'request_accepted' && acceptedChannels.length) {
      if (isEnabled()) {
        safelyWriteStorage(
          'sessionStorage',
          marketingOptInCaptureStorageKey,
          JSON.stringify({ path: window.location.pathname, captured_at: Date.now() })
        );
      }
      capture(
        'website_form_submitted',
        { ...baseProperties, confirmation_state: 'requested_not_confirmed' },
        { transport: 'sendBeacon', send_instantly: true }
      );
    }
  }

  // Scratch card funnel (assets/proda-scratch-offer.js). Only allowlisted steps, flavor IDs and entry points pass.
  const scratchEvents = { open: 'scratch_card_opened', pick: 'scratch_card_flavor_picked', reveal: 'scratch_card_revealed', dismiss: 'scratch_card_dismissed' };
  const scratchFlavors = new Set(['classic-orange', 'cherry-lime', 'strawberry-lemonade', 'lemon-lime', 'golden-apple', 'root-beer', 'shirley-temple']);
  function handleScratchEvent(event) {
    const detail = event.detail && typeof event.detail === 'object' ? event.detail : {};
    const eventName = scratchEvents[detail.step];
    if (!eventName) return;
    const flavor = scratchFlavors.has(detail.flavor) ? detail.flavor : '';
    const source = ['auto', 'banner', 'link'].includes(detail.source) ? detail.source : '';
    capture(eventName, {
      form_id: 'proda-15-off-fallback',
      offer_variant: 'scratch',
      placement: 'offer_dialog',
      ...(flavor ? { flavor } : {}),
      ...(source ? { open_source: source } : {}),
    });
  }

  function handleCartLinesUpdate(event) {
    if (processedCartEvents.has(event)) return;
    processedCartEvents.add(event);

    const eventTarget = event.target instanceof Element ? event.target : null;
    const formComponent = eventTarget?.closest('product-form-component');
    const lines = Array.isArray(event.lines) ? event.lines : [];
    const action = String(event.action || 'update');
    const source = sourceFromElement(formComponent || eventTarget);
    const quantityAdded = lines.reduce((total, line) => total + numberFrom(line.quantity), 0);
    const firstLine = lines[0] || {};
    const sourceProduct = formComponent ? productFromElement(formComponent) : {};
    const purchase = purchaseFromForm(formComponent);
    const sourceId = String(formComponent?.id || '');
    const sourceVariantId = String(
      formComponent?.dataset.variantId || firstLine.merchandiseId || (action === 'add' ? context.variant_id : '') || ''
    );
    const sourceLineId = String(firstLine.id || '');
    const previousSnapshot = cartState;
    const previousLine = previousSnapshot.items.find(
      (item) => String(item.key || item.id || '') === sourceLineId
    );
    const stableVariantId = String(previousLine?.variant_id || previousLine?.id || sourceVariantId || '');
    const stableSellingPlanId = String(
      previousLine?.selling_plan_allocation?.selling_plan?.id || purchase.selling_plan_id || ''
    );

    const finish = async (result = {}) => {
      const detail = result.detail || {};
      if (detail.didError) return;
      const snapshot = await fetchCartState({ fresh: true });
      void persistCheckoutHandoff({ keepalive: true });

      if (action === 'add') {
        const addedItem = snapshot.items.find((item) => {
          if (String(item.variant_id || item.id || '') !== sourceVariantId) return false;
          const itemPlanId = String(item.selling_plan_allocation?.selling_plan?.id || '');
          return purchase.selling_plan_id ? itemPlanId === purchase.selling_plan_id : itemPlanId === '';
        });
        const unitPriceCents = numberFrom(
          addedItem?.final_price,
          numberFrom(formComponent?.dataset.valueCents, numberFrom(context.product_price_cents))
        );
        const actualQuantityAdded = quantityAdded || numberFrom(detail.itemCount, 1);

        capture('product_added_to_cart', {
          ...sourceProduct,
          product_id: String(sourceProduct.product_id || detail.productId || context.product_id || ''),
          variant_id: sourceVariantId,
          quantity_added: actualQuantityAdded,
          case_quantity: actualQuantityAdded,
          can_count: actualQuantityAdded * 12,
          unit_price_cents: unitPriceCents,
          value_cents: unitPriceCents * actualQuantityAdded,
          source,
          source_id: sourceId,
          ...purchase,
          cart_item_count: snapshot.cart_item_count,
          cart_value_cents: snapshot.cart_value_cents,
          cart_contains_subscription: snapshot.cart_contains_subscription,
          ...shippingThresholdProperties(snapshot),
        });
        return;
      }

      const isRemoval = action === 'remove' || action === 'clear';
      const nextLine = isRemoval
        ? null
        : findMatchingCartLine(snapshot.items, {
            lineId: sourceLineId,
            variantId: stableVariantId,
            sellingPlanId: stableSellingPlanId,
            previousLine,
          });
      const transition = cartQuantityTransition({
        action,
        requestedQuantity: firstLine.quantity,
        previousLineQuantity: previousLine?.quantity,
        resultingLineQuantity: nextLine?.quantity,
        previousCartItemCount: previousSnapshot.cart_item_count,
        nextCartItemCount: snapshot.cart_item_count,
      });

      capture('cart_updated', {
        action: transition.isRemoval ? 'remove' : 'update_quantity',
        source,
        ...cartLineFacts(nextLine || previousLine),
        previous_quantity: transition.previousQuantity,
        new_quantity: transition.newQuantity,
        quantity_delta: transition.quantityDelta,
        requested_quantity: Math.max(0, numberFrom(firstLine.quantity)),
        resulting_line_quantity: Math.max(0, numberFrom(nextLine?.quantity)),
        quantity: transition.newQuantity,
        case_quantity: transition.newQuantity,
        can_count: transition.newQuantity * 12,
        line_value_cents: nextLine
          ? numberFrom(nextLine.final_line_price, numberFrom(nextLine.line_price))
          : 0,
        cart_item_count: snapshot.cart_item_count,
        cart_value_cents: snapshot.cart_value_cents,
        cart_contains_subscription: snapshot.cart_contains_subscription,
        ...shippingThresholdProperties(snapshot),
      });
    };

    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(finish).catch(() => {});
    } else {
      void finish({ detail: event.detail?.data || event.detail || {} });
    }
  }

  function handleCartDiscountUpdate(event) {
    if (processedDiscountEvents.has(event)) return;
    processedDiscountEvents.add(event);

    const component = event.target instanceof Element ? event.target.closest('cart-discount-component') : null;
    const previousDiscountCount = component?.querySelectorAll('.cart-discount__pill').length || 0;
    const requestedDiscountCount = Array.isArray(event.discountCodes) ? event.discountCodes.length : 0;
    const action = requestedDiscountCount > previousDiscountCount ? 'apply' : 'remove';
    const source = component?.closest('theme-drawer#cart-drawer') ? 'cart_drawer' : 'cart_page';

    const captureResult = async (result = {}) => {
      const detail = result.detail || result;
      const resultCart = result.cart || detail.cart || null;
      const resultSource = detail.source === 'proda-earned-offer' ? 'offer_dialog' : source;
      const discounts = Array.isArray(resultCart?.discountCodes) ? resultCart.discountCodes : [];
      const applicableDiscountCount = discounts.filter((discount) => discount?.applicable === true).length;
      const rejectedDiscountCount = discounts.filter((discount) => discount?.applicable === false).length;
      const snapshot = await fetchCartState({ fresh: true });

      capture('cart_discount_updated', {
        action,
        source: resultSource,
        outcome: rejectedDiscountCount > 0 ? 'rejected' : 'accepted',
        previous_discount_count: previousDiscountCount,
        requested_discount_count: requestedDiscountCount,
        applicable_discount_count: applicableDiscountCount,
        rejected_discount_count: rejectedDiscountCount,
        cart_item_count: snapshot.cart_item_count,
        cart_value_cents: snapshot.cart_value_cents,
        cart_contains_subscription: snapshot.cart_contains_subscription,
        ...shippingThresholdProperties(snapshot),
      });
    };

    if (event.promise && typeof event.promise.then === 'function') {
      event.promise.then(captureResult).catch((error) => {
        if (error?.name === 'AbortError') return;
        capture('cart_discount_updated', {
          action,
          source,
          outcome: 'error',
          previous_discount_count: previousDiscountCount,
          requested_discount_count: requestedDiscountCount,
        });
      });
    }
  }

  function handleCartError(event) {
    const eventTarget = event.target instanceof Element ? event.target : null;
    capture('cart_error', {
      source: sourceFromElement(eventTarget),
      source_id: String(eventTarget?.id || event.detail?.sourceId || ''),
      error_code: String(event.code || event.detail?.code || 'unknown'),
    });
  }

  function handleProductSelect(event) {
    if (!event.promise || typeof event.promise.then !== 'function') return;

    event.promise
      .then(({ variant }) => {
        if (!variant) return;
        capture('product_variant_selected', {
          product_id: String(event.product?.id || context.product_id || ''),
          product_handle: String(event.product?.handle || context.product_handle || ''),
          variant_id: String(variant.id || ''),
          variant_available: Boolean(variant.availableForSale),
          value_cents: Math.round(numberFrom(variant.price?.amount) * 100),
        });
        window.requestAnimationFrame(capturePurchaseOptionsViewed);
      })
      .catch(() => {});
  }

  function handlePurchaseOptionChange(event) {
    const detail = event.detail || {};
    const properties = {
      product_id: String(detail.productId || context.product_id || ''),
      product_handle: String(detail.productHandle || context.product_handle || ''),
      variant_id: String(detail.variantId || context.variant_id || ''),
      purchase_type: detail.purchaseType === 'subscription' ? 'subscription' : 'one_time',
      case_quantity: Math.max(1, numberFrom(detail.caseQuantity, 1)),
      can_count: Math.max(12, numberFrom(detail.canCount, 12)),
      unit_price_cents: numberFrom(detail.unitPriceCents),
      value_cents: numberFrom(detail.valueCents),
      selling_plan_id: String(detail.sellingPlanId || ''),
      selling_plan_name: String(detail.sellingPlanName || ''),
      savings_percent: numberFrom(detail.savingsPercent),
      source: 'pdp',
    };

    capture('purchase_option_selected', properties);
  }

  function handlePackSizeChange(event) {
    const detail = event.detail || {};
    capture('pack_size_selected', {
      product_id: String(detail.productId || context.product_id || ''),
      product_handle: String(detail.productHandle || context.product_handle || ''),
      variant_id: String(detail.variantId || context.variant_id || ''),
      purchase_type: detail.purchaseType === 'subscription' ? 'subscription' : 'one_time',
      ...(detail.sellingPlanId ? { selling_plan_id: String(detail.sellingPlanId) } : {}),
      savings_percent: numberFrom(detail.savingsPercent),
      case_quantity: Math.max(1, numberFrom(detail.caseQuantity, 1)),
      can_count: Math.max(12, numberFrom(detail.canCount, 12)),
      unit_price_cents: numberFrom(detail.unitPriceCents),
      value_cents: numberFrom(detail.valueCents),
      source: 'pdp',
    });
  }

  function handleCollectionUpdate(event) {
    const filters = Array.isArray(event.productFilters) ? event.productFilters : [];
    const properties = {
      collection_id: event.collection?.id ? String(event.collection.id) : String(context.collection_id || ''),
      collection_handle: String(event.collection?.handle || context.collection_handle || ''),
      filter_count: filters.length,
      filter_types: productFilterTypes(filters),
      sort_key: safeLabel(event.sortKey || 'collection_default', 40),
    };

    if (!event.promise || typeof event.promise.then !== 'function') {
      capture('collection_results_updated', properties);
      return;
    }

    event.promise
      .then((result = {}) => {
        const detail = result.detail || result;
        capture('collection_results_updated', {
          ...properties,
          result_count: Math.max(0, numberFrom(detail.productsCount)),
        });
      })
      .catch(() => {});
  }

  function handleSearchUpdate(event) {
    const filters = Array.isArray(event.search?.productFilters) ? event.search.productFilters : [];
    const properties = {
      search_query_length: String(event.search?.query || '').trim().length,
      filter_count: filters.length,
      filter_types: productFilterTypes(filters),
      sort_key: safeLabel(event.search?.sortKey || 'relevance', 40),
    };

    if (!event.promise || typeof event.promise.then !== 'function') {
      capture('search_results_updated', properties);
      return;
    }

    event.promise
      .then((result = {}) => {
        const detail = result.detail || result;
        capture('search_results_updated', {
          ...properties,
          result_count: Math.max(0, numberFrom(detail.totalCount)),
        });
      })
      .catch(() => {});
  }

  function handleCartViewEvent(event) {
    const source =
      event.target instanceof Element && event.target.closest('theme-drawer#cart-drawer') ? 'cart_drawer' : 'cart_page';
    void captureCartViewed(source);
  }

  function handleDrawerOpen(event) {
    const drawer = event.target instanceof Element ? event.target.closest('theme-drawer#cart-drawer') : null;
    if (!drawer) return;
    markSemanticAutocaptureBoundaries(drawer);
    window.requestAnimationFrame(() => captureUpsellImpressions());
  }

  function handleDrawerClose(event) {
    const drawer = event.target instanceof Element ? event.target.closest('theme-drawer#cart-drawer') : null;
    if (!drawer || Date.now() - checkoutIntentAt < 10000) return;

    window.setTimeout(async () => {
      const snapshot = await fetchCartState();
      if (snapshot.cart_item_count < 1) return;
      capture('cart_drawer_closed_with_items', {
        source: 'cart_drawer',
        cart_item_count: snapshot.cart_item_count,
        cart_value_cents: snapshot.cart_value_cents,
        cart_contains_subscription: snapshot.cart_contains_subscription,
        ...shippingThresholdProperties(snapshot),
      });
    }, 0);
  }

  function start() {
    if (analytics.consentResolved !== true) return;
    if (!isEnabled()) {
      void clearCheckoutHandoff();
      return;
    }
    bindFeatureFlags();
    window.PRODA_CAPTURE = capture;
    markSemanticAutocaptureBoundaries();
    bindCheckoutHandoff();
    if (cartState.cart_item_count > 0 && cartState.items.length === 0) void fetchCartState();
    capturePageContext();
    capturePurchaseOptionsViewed();
    captureProductCardImpressions();
    captureLeadFormImpressions();
    bindOfferFallbackClose();
    bindScrollDepthTracking();
  }

  document.addEventListener('click', handleClick, { capture: true });
  document.addEventListener('submit', handleSubmit, { capture: true });
  document.addEventListener('shopify:cart:lines-update', handleCartLinesUpdate);
  document.addEventListener('shopify:cart:discount-update', handleCartDiscountUpdate);
  document.addEventListener('shopify:cart:error', handleCartError);
  document.addEventListener('shopify:cart:view', handleCartViewEvent);
  document.addEventListener('shopify:collection:update', handleCollectionUpdate);
  document.addEventListener('shopify:search:update', handleSearchUpdate);
  document.addEventListener('shopify:product:select', handleProductSelect);
  document.addEventListener('theme-drawer:open', handleDrawerOpen);
  document.addEventListener('theme-drawer:close', handleDrawerClose);
  document.addEventListener('proda:purchase-option-change', handlePurchaseOptionChange);
  document.addEventListener('proda:pack-size-change', handlePackSizeChange);
  document.addEventListener('proda:marketing-opt-in-result', handleMarketingOptInResult);
  document.addEventListener('proda:scratch', handleScratchEvent);
  document.addEventListener('proda:analytics:ready', start);
  document.addEventListener('proda:privacy:changed', () => {
    if (isEnabled()) void persistCheckoutHandoff({ keepalive: true });
  });
  document.addEventListener('shopify:section:load', (event) => {
    markSemanticAutocaptureBoundaries(event.target);
    captureProductCardImpressions(event.target);
    captureLeadFormImpressions(event.target);
  });
  document.addEventListener('proda:analytics:disabled', () => {
    pageContextCaptured = false;
    observedPurchaseOptions = new WeakMap();
    observedProductCards = new WeakSet();
    observedLeadForms = new WeakSet();
    scrollDepthMilestones.clear();
    upsellImpressions.clear();
    capturedExperimentAssignments.clear();
    productCardObserver?.disconnect();
    leadFormObserver?.disconnect();
    productCardObserver = null;
    leadFormObserver = null;
    lastCartViewSignature = '';
    lastCartViewAt = 0;
    void clearCheckoutHandoff();
  });
  document.addEventListener('proda:experiments:ready', () => {
    void persistCheckoutHandoff({ keepalive: true });
  });
  document.addEventListener('proda:experiments:assignments-changed', handleExperimentAssignment);
  window.addEventListener('klaviyoForms', handleKlaviyoForm);

  start();
}
